/* Aurie storefront behaviour: offer selector, AJAX add-to-cart, sticky bar, gallery, in-view videos.
   Everything enhances native HTML: with JS off, the product form still posts to /cart/add. */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, format) {
    const value = (Number(cents) || 0) / 100;
    const withDelims = (n, decimals, thousands, decimal) => {
      const [whole, frac] = n.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  }

  function initBuyBox(section) {
    const form = section.querySelector('form[id^="LumForm-"]');
    if (!form) return;

    let moneyFormat = '${{amount}}';
    try { moneyFormat = JSON.parse(section.querySelector('[data-lum-money-format]').textContent); } catch (e) {}

    const radios = Array.from(form.querySelectorAll('input[type="radio"][data-price]'));
    const priceEl = section.querySelector('[data-lum-price]');
    const priceNote = section.querySelector('[data-lum-price-note]');
    const submit = form.querySelector('[data-lum-submit]');
    const submitText = form.querySelector('[data-lum-submit-text]');
    const qtyInput = form.querySelector('[data-lum-qty]');
    const errorEl = form.querySelector('[data-lum-error]');
    const sticky = section.querySelector('[data-lum-sticky]');
    const stickyPrice = section.querySelector('[data-lum-sticky-price]');
    const stickyBtn = section.querySelector('[data-lum-sticky-btn]');

    function selected() { return radios.find((r) => r.checked) || null; }

    function update() {
      const r = selected();
      radios.forEach((x) => x.closest('.lum-offer')?.classList.toggle('is-checked', x === r));
      if (!r) return;
      const price = formatMoney(r.dataset.price, moneyFormat);
      if (priceEl) priceEl.textContent = price;
      if (priceNote) priceNote.hidden = !('bundle' in r.dataset);
      if (submitText) submitText.textContent = `${submit.dataset.label} — ${price}`;
      if (stickyPrice) stickyPrice.textContent = price;
      if (qtyInput) qtyInput.value = r.dataset.qty || '1';
      if (r.dataset.mediaId) section.dispatchEvent(new CustomEvent('lum:media', { detail: r.dataset.mediaId }));
    }
    radios.forEach((r) => r.addEventListener('change', update));
    update();

    let busy = false;
    let idleText = '';
    function setLoading(on) {
      busy = on;
      [submit, stickyBtn].forEach((b) => {
        if (!b) return;
        b.classList.toggle('is-loading', on);
        b.setAttribute('aria-disabled', on ? 'true' : 'false');
        b.setAttribute('aria-busy', on ? 'true' : 'false');
      });
      if (submitText) {
        if (on) { idleText = submitText.textContent; submitText.textContent = 'Adding…'; }
        else if (idleText) { submitText.textContent = idleText; }
      }
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || '';
      errorEl.classList.toggle('is-visible', Boolean(msg));
    }

    form.addEventListener('submit', async (event) => {
      if (!window.fetch || !window.FormData) return; // native post
      event.preventDefault();
      if (busy) return; // prevent duplicate submissions
      showError('');
      setLoading(true);

      const data = new FormData(form);
      data.delete('lum_offer');
      const drawer = document.querySelector('cart-drawer');
      const canRender = drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function';
      if (canRender) {
        data.append('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
        data.append('sections_url', window.location.pathname);
      }

      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          showError(json.description || json.message || 'Something went wrong. Please try again.');
          return;
        }
        if (canRender && json.sections) {
          drawer.classList.contains('is-empty') && drawer.classList.remove('is-empty');
          drawer.renderContents(json);
        } else {
          window.location.href = `${root}cart`;
        }
      } catch (e) {
        form.submit(); // fall back to a normal form post
      } finally {
        setLoading(false);
      }
    });

    // Sticky bar: visible once the main button has scrolled above the viewport.
    if (sticky && submit && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        document.body.classList.toggle('lum-has-sticky', show);
      }).observe(submit);
    }
  }

  function initGallery(section) {
    const track = section.querySelector('[data-lum-track]');
    if (!track) return;
    const slides = Array.from(track.children);
    const controls = Array.from(section.querySelectorAll('[data-lum-goto]'));

    function goTo(i) {
      const slide = slides[i];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    function mark(i) {
      controls.forEach((c) => c.setAttribute('aria-current', String(Number(c.dataset.lumGoto) === i)));
    }
    controls.forEach((c) => c.addEventListener('click', () => goTo(Number(c.dataset.lumGoto))));

    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => mark(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))));
    }, { passive: true });

    section.addEventListener('lum:media', (e) => {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(e.detail));
      if (i > -1) goTo(i);
    });
  }

  function initInviewVideos(scope) {
    const videos = Array.from(scope.querySelectorAll('[data-lum-inview] video'));
    if (!videos.length) return;
    videos.forEach((v) => { v.muted = true; v.setAttribute('muted', ''); v.playsInline = true; });
    if (reduceMotion || !('IntersectionObserver' in window)) {
      videos.forEach((v) => v.setAttribute('controls', ''));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          if (target.preload === 'none') target.preload = 'metadata';
          const p = target.play();
          if (p && p.catch) p.catch(() => target.setAttribute('controls', ''));
        } else {
          target.pause();
        }
      });
    }, { threshold: 0.5 });
    videos.forEach((v) => io.observe(v));
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-lum-buy]').forEach((s) => { initBuyBox(s); initGallery(s); });
    initInviewVideos(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-init a section after it is re-rendered.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
